import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import WarningBlueIcon from '_assets/icons/ic-warning-blue.svg'
import { IconButton, Loader, Button as ReferaButton } from '@refera/ui-web'
import * as Modal from '_components/modal/generic-modal'
import { CloseCircle as CloseCircleIcon, RotateRight } from '@refera/ui-icons'

import Theme from '@refera/ui-core'
import { Box, Grid, Tooltip as MuiTooltip, Button, Rating } from '@mui/material'
import { Typography, TextField } from '@material-ui/core'

import CommonSelect from '_components/common/select'
import MaterialUIAutocomplete from '@material-ui/lab/Autocomplete'

import NewProviderIcon from '_assets/icons/new-provider.svg'
import Svg from '_components/svg'
import { currentServiceOrderSelector } from '_modules/service-orders/selectors'
import {
  getProviderSuggestionsListSelector,
  getProviderSuggestionsListSelectorLoading,
  providerSuggestionsSelector,
  providersResultWithoutPaginationSelector,
} from '_modules/provider/selectors'
import {
  getProviderSuggestionsList,
  getProvidersV2WithoutPagination,
} from '_modules/provider/actions'
import { REGISTER_STATUS, ROUTES } from '_utils/constants'
import { isAgencySaas, reorder } from '_utils/helpers'

import useStyles from './styles'
import { createTradesmanSequence } from '_modules/tradesman-sequence/actions'
import { getServiceOrder, getGenericParameters } from '_modules/service-orders/actions'
import Loading from '_/components/loading'
import { GENERIC_PARAMETERS } from '_/utils/constants/service-order'
import useIsGenericParamActive from '_/hooks/use-is-generic-param-active'

import { useDialog } from '_/hooks/use-dialog'
import { LEVEL_OPTIONS } from '_/views/provider/components/ProviderReferaDetailsModal/constants'
import { getStepStatusLog } from '_/modules/budget/actions'
import FilterLabel from './filter-label'
import DragAndDrop from '_/components/common/drag-and-drop'
import { useToast } from '_/hooks/use-toast'
import { formatErrorMessage } from '_/views/finance/utils/FormatErrorMessage'
import Tooltip from '_/components/tooltip'
import { PROVIDER_ORIGIN_OPTIONS, getProviderLimit } from './constants'
import { Autocomplete } from '_/components/inputs'
import { buildAddProviderModalV2ProviderList } from './utils'
import HelpInfoButton from '_/components/button/help-info-button'
import Chip from '_components/chip'
import moment from 'moment'

const EMPTY_ROUND = { name: null, value: null }
const ROUNDS_OPTIONS = [
  { label: '0', value: 0 },
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
]
const DATE_FORMATS = [
  'YYYY-MM-DD',
  'YYYY-MM-DDTHH:mm:ssZ',
  'DD/MM/YYYY',
  'DD/MM/YYYY HH:mm:ss',
  'DD/MM/YYYY HH:mm',
]

const INITIAL_STATE = {
  tradesman: [EMPTY_ROUND, EMPTY_ROUND, EMPTY_ROUND, EMPTY_ROUND],
}

/**
 * @typedef {Object} EndActionFnParams
 * @property {'success' | 'error' | 'unfinished'} [EndActionFnParams.reason] - The reason for ending the action.
 * @property {string} [EndActionFnParams.message] - The optional message for the action.
 */

/**
 * @param {Object} props
 * @param {(params?: EndActionFnParams) => void} props.handleClose
 * @param {Array} props.defaultProviderOrigin
 * @param {boolean} props.supressSubmitSideEffects - Whether to supress submit side effects. Defaults to false.
 */
const AddProviderQueueModalV2 = ({
  handleClose,
  defaultProviderOrigin,
  supressSubmitSideEffects = false,
}) => {
  const dispatch = useDispatch()
  const { showToast } = useToast()
  const serviceOrder = useSelector(currentServiceOrderSelector)
  const tradesmanList = useSelector(providersResultWithoutPaginationSelector)
  const providerSuggestionsList = useSelector(getProviderSuggestionsListSelector)
  const isCategoriesOptionsLoading = useSelector(getProviderSuggestionsListSelectorLoading)
  const [isLoading, setIsLoading] = useState(false)
  const [rounds, setRounds] = useState([EMPTY_ROUND])
  const suggestedProviders = useSelector(providerSuggestionsSelector)
  const distributionActive = useIsGenericParamActive({
    name: GENERIC_PARAMETERS.DISTRIBUTION,
  })
  const { showDialog, closeDialog } = useDialog()

  const [ratingSort, setRatingSort] = useState(null)
  const [previousTradesmanIds, setPreviousTradesmanIds] = useState([])
  const [providersList, setProvidersList] = useState(tradesmanList)
  const [isFiltered, setIsFiltered] = useState(false)

  useEffect(() => {
    if (!isFiltered && tradesmanList) {
      setProvidersList(tradesmanList)
    }
  }, [tradesmanList, isFiltered])

  const isSaas = isAgencySaas(serviceOrder?.agency)

  const providerSuggestionsListOptions = useMemo(() => {
    let list = providerSuggestionsList
    if (!list?.length && tradesmanList?.length) {
      list = tradesmanList.slice(0, 5)
    }

    const defaultSuggestionsRounds = list?.map((item, index) => {
      return { name: `rounds.${index}`, value: isSaas ? 1 : item.round || 0 }
    })

    return {
      tradesman: list?.map((item, index) => {
        return { name: `tradesman.${index}`, value: item }
      }),
      rounds: defaultSuggestionsRounds,
    }
  }, [providerSuggestionsList, tradesmanList, isSaas])

  useEffect(() => {
    if (providerSuggestionsListOptions?.rounds) {
      setRounds(providerSuggestionsListOptions.rounds)
    }
  }, [providerSuggestionsListOptions])

  const { control, getValues, watch, handleSubmit, reset, ...methods } = useForm({
    defaultValues:
      providerSuggestionsListOptions?.tradesman?.length > 0
        ? providerSuggestionsListOptions
        : INITIAL_STATE,
  })

  const headerFieldsMethods = useForm()

  const providerOriginValue = useMemo(() => {
    return headerFieldsMethods.getValues('providerOrigin') || []
  }, [headerFieldsMethods.watch('providerOrigin')])

  const {
    fields: tradesmanFields,
    remove: removeTradesman,
    append,
  } = useFieldArray({
    control,
    name: 'tradesman',
  })

  const poolAvailable = useMemo(() => {
    return !!serviceOrder?.runPool
  }, [serviceOrder])

  const styles = useStyles({ poolAvailable, multipleTradesman: tradesmanFields.length > 1 })

  const removeRounds = index => {
    const newRounds = rounds.filter((_, i) => i !== index)
    setRounds(newRounds)
  }

  const handleRemoveItem = useCallback(
    index => {
      removeTradesman(index)
      removeRounds(index)
    },
    [removeTradesman, removeRounds]
  )

  const canRemoveItem = useMemo(() => tradesmanFields.length > 1, [tradesmanFields])

  const handleRatingSort = () => {
    setRatingSort(prev => (prev === null ? 'asc' : prev === 'asc' ? 'desc' : null))
  }

  const providerOptions = useMemo(() => {
    return providersList?.map(item => ({
      value: item,
      name: item.name,
      label: item.name,
    }))
  }, [providersList])

  const getOptionLabel = useCallback(
    option => {
      return (
        providerOptions?.find(opt => opt?.value?.tradesmanId === option?.value?.tradesmanId)
          ?.label || ''
      )
    },
    [providerOptions]
  )

  const getOptionSelected = useCallback((option, currentValue) => {
    return Number(option?.value?.tradesmanId) === Number(currentValue?.value?.tradesmanId)
  }, [])

  const onInputChange = useCallback(
    (inputLabel, name, onChange, index) => {
      const isValidOption = providerOptions.find(option => option.name === inputLabel) || null

      if (isValidOption) {
        const suggestedProvider = providerSuggestionsListOptions?.tradesman?.find(
          item => item.name === name
        )?.value
        const roundName = rounds?.[index]?.name
        const roundNameIndex = roundName?.split('.')?.[1]
        const changedRoundIndex = roundNameIndex !== String(index)

        if (suggestedProvider && Object.hasOwn(suggestedProvider, 'round')) {
          if (changedRoundIndex) {
            setRounds(prevState => {
              const newRounds = [...prevState]
              newRounds[index] = { ...prevState[index], name: `rounds.${index}` }
              return newRounds
            })
          }
        }

        onChange({ name, value: isValidOption.value })
      }
      if (inputLabel === '') {
        setRounds(prevState => {
          const newRounds = [...prevState]
          newRounds[index] = { name: `rounds.${index}`, value: '' }
          return newRounds
        })
        onChange({ name: null, value: EMPTY_ROUND })
      }
    },
    [providerOptions, providerSuggestionsListOptions, rounds]
  )

  const handleRoundsChange = (roundItem, index) => {
    const { target } = roundItem
    setRounds(prevState => {
      const newRounds = [...prevState]
      newRounds[index] = { name: `rounds.${index}`, value: target.value }
      return newRounds
    })
  }

  const getLevelLabel = useCallback(level => {
    return LEVEL_OPTIONS.find(itemLevel => itemLevel?.value === level)?.label || ''
  })

  useEffect(() => {
    const initialIds = tradesmanFields.map(field => field?.value?.tradesmanId).filter(Boolean)

    if (initialIds.length > 0) {
      setPreviousTradesmanIds(initialIds)
    }
  }, [tradesmanFields])

  const ProviderEvaluationContent = ({ item }) => {
    const { level, servicesCount, warrantyPercentage, budgetConversion } = item?.value || {}

    const hasData = Object.values({
      level,
      servicesCount,
      warrantyPercentage,
      budgetConversion,
    }).some(v => v !== null && v !== undefined && v !== '' && v !== 0)

    if (!hasData) {
      return (
        <Typography variant="body2" style={{ textAlign: 'center', padding: '4px 8px' }}>
          Prestador não realizou serviços o suficiente com a Refera para gerar classificação.
        </Typography>
      )
    }

    return (
      <>
        <Typography variant="body2">
          <strong>Classificação:</strong> {getLevelLabel(level) || '-'}
        </Typography>
        <Typography variant="body2">
          <strong>Qtd. de serviços realizados:</strong> {servicesCount ?? '-'}
        </Typography>
        <Typography variant="body2">
          <strong>% de garantias:</strong> {warrantyPercentage ?? '-'}%
        </Typography>
        <Typography variant="body2">
          <strong>Conv. de orçamentos:</strong> {budgetConversion ?? '-'}%
        </Typography>
      </>
    )
  }

  const getProviderFromList = useCallback(
    (selectedProvider, itemField) => {
      if (!providersList) return undefined

      const id = selectedProvider?.tradesmanId ?? itemField?.value?.tradesmanId
      if (id == null) return undefined
      return providersList.find(p => p?.tradesmanId === id)
    },
    [providersList]
  )

  const getProviderRating = useCallback(
    (index, ratingType) => {
      const selectedProvider = getValues()?.tradesman?.[index]?.value
      const itemField = tradesmanFields[index]
      const providerFromList = getProviderFromList(selectedProvider, itemField)

      return (
        providerFromList?.[ratingType]?.average ??
        selectedProvider?.[ratingType]?.average ??
        itemField?.value?.[ratingType]?.average ??
        0
      )
    },
    [getProviderFromList, getValues, tradesmanFields]
  )

  const renderRatingCell = useCallback(
    (index, ratingType, xs) => (
      <Grid item xs={xs} className={styles.fieldGrid}>
        <Rating
          value={getProviderRating(index, ratingType)}
          precision={0.5}
          readOnly
          size="medium"
        />
      </Grid>
    ),
    [getProviderRating, styles.fieldGrid]
  )

  const isProviderNew = useCallback(signupDate => {
    if (!signupDate) return false
    const date = moment(signupDate, DATE_FORMATS, true)
    return date.isValid() && moment().diff(date, 'days') <= 30
  }, [])

  const renderSelect = useCallback(
    (item, index) => (
      <>
        <Grid item className={styles.fullWidth} display="flex" gap="1rem">
          {/* Selecione o Prestador */}
          <Grid item xs={3.9}>
            <div
              style={{
                flex: '1 1 auto',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minWidth: 0,
              }}
            >
              <Controller
                control={control}
                name={`tradesman.${index}`}
                defaultValue={item}
                render={({ onChange, name, ...fieldProps }) => {
                  return (
                    <MaterialUIAutocomplete
                      {...fieldProps}
                      id={name}
                      options={providerOptions}
                      style={{ width: '100%' }}
                      onChange={(event, newValue) => {
                        return onChange({ name, value: newValue.value })
                      }}
                      onInputChange={(event, newInputValue) => {
                        return onInputChange(newInputValue, name, onChange, index)
                      }}
                      multiple={false}
                      getOptionLabel={getOptionLabel}
                      getOptionSelected={getOptionSelected}
                      disableClearable
                      renderInput={params => {
                        const selectedProvider = getValues()?.tradesman?.[index]?.value
                        const itemField = tradesmanFields[index]
                        const providerFromList = getProviderFromList(selectedProvider, itemField)
                        const signupDate =
                          providerFromList?.providerSignupDate ??
                          selectedProvider?.providerSignupDate
                        const isNew = isProviderNew(signupDate)
                        return (
                          <TextField
                            {...params}
                            fullWidth
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {params.InputProps.endAdornment}
                                  {isNew && <Chip label="Novo" className={styles.newChip} />}
                                </>
                              ),
                            }}
                          />
                        )
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.value?.tradesmanId === value?.value?.tradesmanId
                      }
                      renderOption={props => {
                        const { level, servicesCount, warrantyPercentage, budgetConversion } =
                          props.value?.score || {}

                        const levelLabel = level
                          ? LEVEL_OPTIONS.find(itemLevel => itemLevel.value === level)
                          : null

                        if (!level || !levelLabel) {
                          return (
                            <MuiTooltip
                              placement="left"
                              arrow
                              classes={{ popper: styles.tooltip }}
                              title={
                                <Box className={[styles.tooltipBox, styles.center]}>
                                  <Svg className={styles.svgIcon} icon={NewProviderIcon} />
                                  <Typography variant="body2">Prestador novo</Typography>
                                </Box>
                              }
                            >
                              <li {...props} className={styles.li}>
                                {props.label}
                              </li>
                            </MuiTooltip>
                          )
                        }

                        return (
                          <MuiTooltip
                            placement="left"
                            arrow
                            classes={{ popper: styles.tooltip }}
                            title={
                              <Box className={styles.tooltipBox}>
                                <Typography variant="body2">
                                  <strong>Classificação:</strong> {getLevelLabel(levelLabel)}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Qtd. de serviços realizados:</strong> {servicesCount}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>% de garantias:</strong> {warrantyPercentage}%
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Conv. de orçamentos:</strong> {budgetConversion}%
                                </Typography>
                              </Box>
                            }
                          >
                            <li {...props} className={styles.li}>
                              {props.label}
                            </li>
                          </MuiTooltip>
                        )
                      }}
                    />
                  )
                }}
              />
            </div>
          </Grid>

          {/* Rodada */}
          <Grid item xs={1.3}>
            {poolAvailable && (
              <CommonSelect
                name="rounds"
                defaultValue={rounds[index]?.value || 0}
                value={rounds[index]?.value || 0}
                onChange={roundItem => handleRoundsChange(roundItem, index)}
                options={ROUNDS_OPTIONS}
                containerClassName={styles.customSelectContainer}
                labelClassName={styles.hidden}
                selectStyles={styles.customSelect}
              />
            )}
          </Grid>

          {/* Acessar prestador */}
          <Grid item xs={1.8} className={styles.fieldGrid}>
            <a
              href={`${ROUTES.PROVIDER}/${item?.value?.companyId ?? item?.value?.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              Acessar
            </a>
          </Grid>

          {/* Minha avaliação */}
          {renderRatingCell(index, 'agencyAverageRate', 2)}

          {/* Avaliação das imobiliárias */}
          {renderRatingCell(index, 'generalAverageRate', 1.8)}
          {/* Avaliação */}

          <Grid item xs={1.6} className={styles.fieldGrid}>
            <Tooltip
              triggerType="click"
              title={<ProviderEvaluationContent item={item} />}
              tooltipClass={styles.tooltip}
            >
              <Typography className={styles.tooltipTrigger}>
                <Svg icon={WarningBlueIcon} className={styles.supportIcon} />
                <span className={styles.link}>Avaliação</span>
              </Typography>
            </Tooltip>
          </Grid>
        </Grid>

        {canRemoveItem && (
          <Grid item classes={{ item: styles.deleteButtonContainer }}>
            <IconButton
              variant="ghost"
              onClick={() => handleRemoveItem(index)}
              className={styles.deleteButton}
            >
              <CloseCircleIcon color={Theme.Colors.Red.Base} />
            </IconButton>
          </Grid>
        )}
      </>
    ),
    [
      canRemoveItem,
      control,
      handleRemoveItem,
      providerOptions,
      styles.deleteButton,
      styles.deleteButtonContainer,
      styles.fullWidth,
      getOptionLabel,
      getOptionSelected,
      providerSuggestionsListOptions,
      poolAvailable,
      rounds,
      providersList,
      renderRatingCell,
      isProviderNew,
    ]
  )

  const updateList = useCallback(
    (startIndex, endIndex) => {
      if (startIndex === endIndex) return

      const { tradesman: tradesmanValue, runPool } = getValues()
      const reorderedTradesman = reorder(tradesmanValue, startIndex, endIndex)
      const reorderedRounds = reorder(rounds, startIndex, endIndex)

      reset(
        { tradesman: reorderedTradesman, runPool },
        {
          keepErrors: true,
          keepDirty: true,
          keepTouched: true,
          keepSubmitCount: true,
        }
      )
      setRounds(reorderedRounds)
    },
    [getValues, reset, rounds]
  )

  const convertToSlug = useCallback(
    text =>
      text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replaceAll(' ', '_'),
    []
  )

  const handleSubmitFunction = useCallback(
    async data => {
      setIsLoading(true)
      const candidates = data.tradesman
        .filter(field => field?.name)
        .map(field => field.value.tradesmanId)
      const roundsValues = rounds?.map(round => round?.value).filter(round => round !== '')

      await dispatch(
        createTradesmanSequence(serviceOrder.id, {
          isActive: true,
          serviceOrder: serviceOrder.id,
          candidates,
          suggestedProviders,
          runPool: true,
          rounds: roundsValues,
          dontFilterMyProviderContract: true,
        })
      )
        .then(() => {
          if (!supressSubmitSideEffects) {
            dispatch(getServiceOrder(serviceOrder?.id))
            dispatch(getStepStatusLog(serviceOrder?.id))
          }
          handleClose({ reason: 'success' })
        })
        .catch(error => {
          showToast({ message: formatErrorMessage(error), type: 'error' })
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [serviceOrder.id, handleClose, suggestedProviders, rounds, serviceOrder, showToast]
  )

  const handleSave = useCallback(() => {
    if (serviceOrder?.isVipClient) {
      showDialog({
        type: 'info',
        subject: 'Você tem certeza que deseja fazer o pool para o cliente VIP?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          handleSubmit(handleSubmitFunction)()
          closeDialog()
        },
        onCancel: () => {
          closeDialog()
        },
      })
      return
    }

    handleSubmit(handleSubmitFunction)()
  }, [serviceOrder?.isVipClient, rounds, handleSubmitFunction])

  const disableSaveButton = useMemo(
    () =>
      isLoading ||
      getValues()?.tradesman?.every(field => !field?.name) ||
      !Object.keys(getValues()).length,
    [isLoading, watch()]
  )

  const currentFilters = useMemo(() => {
    return serviceOrder
      ? {
          ...(serviceOrder.getIn(['property', 'city']) && {
            cities: convertToSlug(serviceOrder.getIn(['property', 'city'])),
          }),
          ...(serviceOrder.category?.first?.()?.get('id') && {
            mainServices: serviceOrder.category?.first?.()?.get('id'),
          }),
          registrationStatus: REGISTER_STATUS.COMPLETE,
          serviceOrder: serviceOrder.id,
          referaSuggestion: false,
          providerOrigin: providerOriginValue.map(item => item.value),
        }
      : {}
  }, [serviceOrder, providerOriginValue, tradesmanFields.length])

  useEffect(() => {
    !distributionActive &&
      dispatch(
        getGenericParameters({
          name: GENERIC_PARAMETERS.DISTRIBUTION,
        })
      )
  }, [])

  const listProps = useMemo(
    () => ({
      item: true,
      xs: 12,
      classes: { item: styles.fieldList },
      style: { gap: '2.4rem' },
    }),
    [styles.fieldList]
  )

  const itemProps = useMemo(() => ({
    item: true,
    xs: 12,
    columnSpacing: '24',
    wrap: 'nowrap',
    className: styles.tableRow,
  }))

  /** @param {Object} params.payload newProviders */
  const handleGetSuggestions = useCallback(
    async ({ payload }) => {
      let newProvidersList = payload
      if (distributionActive) {
        const res = await dispatch(
          getProviderSuggestionsList({
            providers: payload,
            serviceOrderId: serviceOrder.id,
          })
        )

        newProvidersList = res.providers || []
      }

      const { tradesmanValues: newTradesmanValues, roundsValues: newRoundsValues } =
        buildAddProviderModalV2ProviderList(newProvidersList, isSaas)

      setRounds(newRoundsValues)
      reset({
        tradesman: [...newTradesmanValues],
        rounds: [...newRoundsValues],
      })
    },
    [providerOriginValue, serviceOrder, distributionActive]
  )

  const handleUpdate = useCallback(
    async (nextSet = 0) => {
      const validForm = await headerFieldsMethods.trigger('providerOrigin')

      if (!validForm) return

      try {
        setIsLoading(true)
        setIsFiltered(true)

        const agencyContractType = serviceOrder?.agency?.get('contractType')
        const limit = getProviderLimit(agencyContractType)
        const { providers } = await dispatch(
          getProvidersV2WithoutPagination({
            filters: { ...currentFilters, limit, offset: nextSet },
          })
        )
        const payload = providers.map(provider => provider.tradesmanId)

        if (nextSet === 0) {
          setPreviousTradesmanIds(payload)
          setProvidersList(providers)
        } else {
          setPreviousTradesmanIds(prev => [...new Set([...prev, ...payload])])
          setProvidersList(prev => {
            const combined = [...prev, ...providers]
            return combined.filter(
              (item, index, self) =>
                index === self.findIndex(t => t.tradesmanId === item.tradesmanId)
            )
          })
        }

        await handleGetSuggestions({
          payload: nextSet === 0 ? payload : [...new Set([...previousTradesmanIds, ...payload])],
          isLoadMore: nextSet > 0,
        })
        showToast({
          type: 'success',
          message: `Foram encontrados ${providers.length} prestadores com os filtros selecionados`,
        })
      } catch (error) {
        console.error('[AddProviderQueueModalV2][handleUpdate]: ', error)
        showToast({
          type: 'error',
          message: formatErrorMessage(error),
        })
      } finally {
        setIsLoading(false)
      }
    },
    [currentFilters, handleGetSuggestions, previousTradesmanIds]
  )

  const loadMore = useCallback(() => {
    const listToUse = providersList || tradesmanList || []
    const currentCount = tradesmanFields.length

    if (listToUse.length > currentCount) {
      const nextItems = listToUse.slice(currentCount, currentCount + 5)

      if (nextItems.length === 0) return

      const nextFormItems = nextItems.map(item => ({
        value: item,
      }))
      const nextRounds = nextItems.map((item, index) => ({
        name: `rounds.${currentCount + index}`,
        value: isSaas ? 1 : item.round || 0,
      }))

      append(nextFormItems)
      setRounds(prev => [...prev, ...nextRounds])
    }
  }, [providersList, tradesmanList, tradesmanFields, append])

  if (isCategoriesOptionsLoading) {
    return <Loading />
  }

  return (
    <>
      <form id="add-providers-queue">
        <Modal.Root open onClose={handleClose} className={styles.modalRoot} disableEnforceFocus>
          <Modal.TitleModal>
            <Typography className={styles.title}>Selecionar prestador</Typography>
          </Modal.TitleModal>
          <Typography className={styles.description}>
            Selecione os prestadores que vão participar da sequência de aceite desse chamado, por
            ordem de prioridade.{' '}
          </Typography>
          <Modal.Content className={styles.modalContent}>
            <Grid item xs={12} display="flex">
              <FormProvider {...headerFieldsMethods}>
                <Autocomplete
                  label="Origem do prestador"
                  name="providerOrigin"
                  defaultValue={defaultProviderOrigin}
                  options={PROVIDER_ORIGIN_OPTIONS}
                  getOptionLabel={option => option.label}
                  autocompleteClasses={styles.autocompleteComponentClasses}
                  containerClasses={styles.autocompleteContainerClasses}
                  required
                />
              </FormProvider>
              <Grid
                style={{ display: 'flex', alignItems: 'center' }}
                onClick={() => handleUpdate()}
              >
                <Button className={styles.button}>Aplicar filtro</Button>
              </Grid>
            </Grid>

            <div className={styles.tableContainer}>
              <Grid
                container
                className={styles.tableHeader}
                alignItems="center"
                wrap="nowrap"
                sx={{ width: '100%' }}
              >
                <Grid item xs={3.9}>
                  <Typography>Selecione o prestador</Typography>
                </Grid>
                <Grid item xs={1.2}>
                  <Typography>Rodada</Typography>
                </Grid>

                <Grid item xs={1.6} style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography>Acessar Prestador</Typography>
                </Grid>
                <Grid item xs={1.8} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Typography>
                    Minha avaliação
                    <HelpInfoButton
                      title="Considere avaliação quando houver 1 a 5 estrelas. Se o prestador tiver zero estrelas, quer dizer que ainda não foi avaliado"
                      placement="bottom"
                    />
                  </Typography>
                </Grid>
                <Grid item xs={1.6} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Typography>
                    Avaliação das imobiliárias
                    <HelpInfoButton
                      title="Considere avaliação quando houver 1 a 5 estrelas. Se o prestador tiver zero estrelas, quer dizer que ainda não foi avaliado"
                      placement="bottom"
                    />
                  </Typography>
                </Grid>
                <Grid item xs={1.8} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FilterLabel
                    label="Avaliação Refera"
                    sort={ratingSort}
                    onClick={handleRatingSort}
                  />
                </Grid>
              </Grid>

              <FormProvider {...{ control, getValues, watch, handleSubmit, reset, ...methods }}>
                <DragAndDrop
                  id="add-provider-queue-modal"
                  list={tradesmanFields}
                  renderItem={tradesmanFields?.length > 0 ? renderSelect : null}
                  updateList={updateList}
                  listProps={listProps}
                  itemProps={itemProps}
                  uiStyle="indexRight"
                />
              </FormProvider>
              <div className={styles.footer}>
                <ReferaButton className={styles.loadButton} onClick={loadMore} disabled={isLoading}>
                  <RotateRight className={styles.buttonIcon} />
                  Carregar mais
                </ReferaButton>
              </div>
            </div>
          </Modal.Content>
          <Modal.Actions className={styles.buttonsContainer}>
            <Modal.ButtonRed onClick={handleClose}>Cancelar</Modal.ButtonRed>
            <Modal.ButtonFullBlue
              form="add-providers-queue"
              onClick={handleSave}
              disabled={disableSaveButton}
            >
              Salvar
            </Modal.ButtonFullBlue>
          </Modal.Actions>
        </Modal.Root>
      </form>
      <Loader hasBackdrop open={isLoading} label="Aguarde..." />
    </>
  )
}

AddProviderQueueModalV2.propTypes = {
  handleClose: PropTypes.func.isRequired,
}

export default React.memo(AddProviderQueueModalV2)
